import './hud.css'

export type Tone = 'ok' | 'warn' | 'bad'

export interface HudAction {
  label: string
  onClick(): void
}

export interface HudOptions {
  onToggleFreeze(frozen: boolean): void
  onReset(): void
}

export interface Hud {
  readonly overlay: HTMLElement
  /** Rows are created on first write and keep their insertion order. */
  metric(key: string, value: string, tone?: Tone): void
  setMetricsVisible(visible: boolean): void
  /** The line above the buttons: what to do next, or what went wrong. */
  setNote(text: string, tone?: Tone): void
  /** Buttons owned by the demo. Replaced wholesale on every call. */
  setActions(actions: readonly HudAction[]): void
  relabelAction(index: number, label: string): void
  dispose(): void
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  return node
}

export function createHud(root: HTMLElement, options: HudOptions): Hud {
  const overlay = element('div', 'demo-overlay')

  const metrics = element('div', 'demo-metrics')
  const list = element('dl')
  metrics.append(list)

  const bottom = element('div', 'demo-controls')
  const note = element('div', 'demo-note')
  const actions = element('div', 'demo-actions')
  const fixed = element('div', 'demo-actions')

  const freeze = element('button')
  freeze.type = 'button'
  freeze.textContent = 'Freeze'
  freeze.setAttribute('aria-pressed', 'false')
  freeze.addEventListener('click', () => {
    const next = freeze.getAttribute('aria-pressed') !== 'true'
    freeze.setAttribute('aria-pressed', String(next))
    options.onToggleFreeze(next)
  })

  const reset = element('button')
  reset.type = 'button'
  reset.textContent = 'Reset'
  reset.addEventListener('click', () => {
    freeze.setAttribute('aria-pressed', 'false')
    options.onReset()
  })

  fixed.append(freeze, reset)
  bottom.append(note, actions, fixed)
  overlay.append(metrics, bottom)
  root.append(overlay)

  const rows = new Map<string, { dt: HTMLElement; dd: HTMLElement }>()

  return {
    overlay,

    metric(key, value, tone = 'ok') {
      let row = rows.get(key)
      if (!row) {
        row = { dt: element('dt'), dd: element('dd') }
        row.dt.textContent = key
        list.append(row.dt, row.dd)
        rows.set(key, row)
      }
      if (row.dd.textContent !== value) row.dd.textContent = value
      if (tone === 'ok') row.dd.removeAttribute('data-tone')
      else row.dd.dataset['tone'] = tone
    },

    setMetricsVisible(visible) {
      metrics.hidden = !visible
    },

    setNote(text, tone = 'ok') {
      note.textContent = text
      if (tone === 'ok') note.removeAttribute('data-tone')
      else note.dataset['tone'] = tone
    },

    setActions(next) {
      actions.replaceChildren()
      for (const action of next) {
        const button = element('button')
        button.type = 'button'
        button.textContent = action.label
        button.addEventListener('click', () => {
          action.onClick()
        })
        actions.append(button)
      }
      actions.hidden = next.length === 0
    },

    relabelAction(index, label) {
      const button = actions.children[index]
      if (button) button.textContent = label
    },

    dispose() {
      overlay.remove()
      rows.clear()
    },
  }
}
